import React from 'react';
import { Navigate } from 'react-router-dom';
import { sfAuth } from './SFAuth';
import { SUPER_ADMIN_ROLE } from './roleChecks';

interface RequireRoleProps {
  children: React.ReactNode;
  /** Role claim(s) required. The user must hold at least one of them. */
  role: string | string[];
  /** What to do when the user lacks the role. Defaults to 'redirect'. */
  behavior?: 'forbid' | 'redirect';
  redirectTo?: string;
}

/**
 * Role-authorization guard. Nest INSIDE <RequireAuth> so authentication (and
 * any token refresh) has already resolved before the role claim is read.
 *
 * Renders its children only when the current session holds one of the required
 * role claims; otherwise it redirects (default) or shows the Forbidden route.
 *
 * `superadmin` is a global bypass: it satisfies any role requirement here, since
 * it's the platform's skeleton-key claim.
 */
export const RequireRole: React.FC<RequireRoleProps> = ({
  children,
  role,
  behavior = 'redirect',
  redirectTo = '/forbidden',
}) => {
  const requiredRoles = Array.isArray(role) ? role : [role];
  const isAuthorized = sfAuth.hasRole(SUPER_ADMIN_ROLE) || requiredRoles.some((r) => sfAuth.hasRole(r));

  if (!isAuthorized) {
    if (behavior === 'forbid') {
      return <Navigate to="/forbidden" replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
